import React, { useEffect, useState } from "react";
import "../styles/AnfahrtPage.css";
import { fetchTransitDepartures } from "../lib/wienerLinien";
import { getAnfahrtPage } from "../lib/api";
import { Page, PageHeader, Section, SectionHead, IntroSplit } from "../components/ui";

const MAP_EMBED_URL =
  "https://www.openstreetmap.org/export/embed.html?bbox=16.3128%2C48.1565%2C16.3278%2C48.1665&layer=mapnik&marker=48.162464%2C16.320711";
const MAP_LINK_URL =
  "https://www.openstreetmap.org/?mlat=48.162464&mlon=16.320711#map=18/48.16195/16.32025";

export default function AnfahrtKontaktPage() {
  const [page, setPage] = useState(null);
  const [error, setError] = useState(null);
  const [stops, setStops] = useState([]);
  const [departures, setDepartures] = useState({});
  const [liveError, setLiveError] = useState("");

  // Static page copy, once
  useEffect(() => {
    let cancelled = false;
    getAnfahrtPage()
      .then((res) => !cancelled && setPage(res))
      .catch((err) => {
        console.error("Page data error:", err);
        if (!cancelled) setError(err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Live transit departures, refreshed every 15s
  useEffect(() => {
    let cancelled = false;

    async function loadDepartures() {
      try {
        const data = await fetchTransitDepartures();
        if (!cancelled) {
          setStops(data.stops || []);
          setDepartures(data.departures || {});
          setLiveError("");
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) setLiveError("Echtzeitdaten derzeit nicht verfügbar.");
      }
    }

    loadDepartures();
    const interval = window.setInterval(loadDepartures, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  const map = (
    <>
      <iframe title="OpenStreetMap Karte zur Rothenburgschule" src={MAP_EMBED_URL} loading="lazy" />
      <a className="ak-map-open" href={MAP_LINK_URL} target="_blank" rel="noreferrer">
        In OpenStreetMap öffnen
      </a>
    </>
  );

  return (
    <Page className="ak-page" loading={!page} error={error}>
      <PageHeader
        parent={{ label: "Über Uns", to: "/profil" }}
        current="Anfahrt & Kontakt"
        title={page?.heroTitle || "Anfahrt & Kontakt"}
        subtitle={page?.heroSubtitle}
      />

      {/* Shared intro layout — the map goes in as custom media. The text
          is passed as children (not `text`) because it contains JSX
          line badges, not just a plain string. */}
      <Section intro>
        <IntroSplit media={map} kicker={page?.kicker} title={page?.introTitle}>
          {page?.introText ? <p>{renderTextWithLineBadges(page.introText)}</p> : null}
        </IntroSplit>
      </Section>

      <Section>
        <SectionHead
          title={page?.sectionTitle || "Öffentliche Verkehrsmittel"}
          subtitle={page?.sectionSubtitle}
        />

        {liveError ? <p className="ak-live-error">{liveError}</p> : null}

        <div className="ak-transit-grid">
          {stops.map((item) => (
            <TransitCard
              key={`${item.line}-${item.stop}-${item.direction}-${item.rbl}`}
              item={item}
              departure={departures[item.rbl]}
            />
          ))}
        </div>
      </Section>
    </Page>
  );
}

function TransitCard({ item, departure }) {
  const nextTwo = departure?.next || [];

  return (
    <article className="rs-card ak-transit-card">
      <div className="ak-transit-card__top">
        <LineBadge variant={resolveLineVariant(item)}>{item.line}</LineBadge>
        <span>{item.type}</span>
      </div>

      <h3>{item.stop}</h3>

      {item.direction ? <p className="ak-direction">Richtung {item.direction}</p> : null}

      <div className="ak-countdown">
        <span>Nächste Abfahrten</span>
        {nextTwo.length === 0 ? (
          <strong>—</strong>
        ) : (
          nextTwo.map((dep, i) => (
            <strong key={i}>{dep.countdown <= 0 ? "jetzt" : `${dep.countdown} min`}</strong>
          ))
        )}
      </div>
    </article>
  );
}

// Bevorzugt die vom Backend gelieferte Variante ("bus" | "u6" | "tram").
// Falls die fehlt oder nicht (mehr) zu einer bekannten Klasse passt,
// wird sie aus der Liniennummer selbst abgeleitet, damit die U6/Tram/Bus-
// Einfärbung nie komplett verloren geht.
const KNOWN_VARIANTS = ["bus", "u6", "tram"];

function resolveLineVariant(item) {
  const variant = (item.variant || "").toLowerCase();
  if (KNOWN_VARIANTS.includes(variant)) return variant;

  const line = (item.line || "").toUpperCase().trim();
  if (line.startsWith("U")) return "u6";
  if (/^\d+$/.test(line)) return "tram";
  return "bus";
}

function LineBadge({ children, variant = "bus" }) {
  return <span className={`ak-line ak-line--${variant}`}>{children}</span>;
}

// Verwandelt die Linienaufzählung am Ende eines Satzes
// ("... erreichbar mit U6, 16A, 64B, 62A und 62.") in Badges,
// ohne den Rest des Textes (z. B. Adresse, PLZ) anzufassen.
function renderTextWithLineBadges(text) {
  if (!text) return text;

  const marker = " mit ";
  const markerIndex = text.lastIndexOf(marker);
  if (markerIndex === -1) return text;

  const before = text.slice(0, markerIndex + marker.length);
  const after = text.slice(markerIndex + marker.length);

  const trailingMatch = after.match(/([.!?]+)\s*$/);
  const trailingPunctuation = trailingMatch ? trailingMatch[0] : "";
  const listPart = trailingPunctuation ? after.slice(0, after.length - trailingPunctuation.length) : after;

  const tokens = listPart.split(/\s*(,|und)\s*/).filter((t) => t !== "");

  return (
    <>
      {before}
      {tokens.map((token, i) => {
        if (token === ",") return <React.Fragment key={i}>, </React.Fragment>;
        if (token === "und") return <React.Fragment key={i}> und </React.Fragment>;
        return (
          <LineBadge key={i} variant={resolveLineVariant({ line: token })}>
            {token}
          </LineBadge>
        );
      })}
      {trailingPunctuation}
    </>
  );
}